// Page components used on overlay pages, such as cards

import {
  faMinus,
  faPlus,
  faUserLarge,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { FC, HTMLAttributes, ReactNode, forwardRef, useState } from "react";
import { Link } from "react-router-dom";
import { cn } from "../lib/text-utils";
import { GravatarAvatar } from "./avatars";
import LoadingSpinnerInline from "./spinners";

export interface IOverviewCardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
  title: string;
  showCollapser?: boolean;
  cardClassName?: string;
}

export const OverviewCard = forwardRef<HTMLDivElement, IOverviewCardProps>(
  (
    {
      className,
      cardClassName,
      children,
      title,
      showCollapser = false,
      ...props
    },
    ref
  ) => {
    // Be able to collapse and expand the card.
    const [showContent, setShowContent] = useState(true);

    return (
      <div className={className}>
        <div
          ref={ref}
          className={cn(
            "px-5 py-2 rounded-lg shadow-md bg-slate-50 dark:bg-slate-600 shadow-gray/50 dark:shadow-gray/80",
            showContent ? "px-5 pt-3 pb-4 h-full" : null,
            cardClassName
          )}
          {...props}
        >
          <div
            className={cn(
              "flex items-center justify-between group hover:cursor-pointer",
              showContent && "pb-3"
            )}
            onClick={() => setShowContent((val) => !val)}
          >
            <h2 className={cn("pb-0 font-bold", showContent ? "text-xl": "text-base" )}>{title}</h2>
            <FontAwesomeIcon
              icon={showContent ? faMinus : faPlus}
              className="w-6 h-6 p-1 rounded-full group-hover:bg-slate-200 dark:group-hover:bg-slate-500 text-slate-500 dark:text-slate-800"
            />
          </div>
          {showContent ? children : null}
        </div>
      </div>
    );
  }
);

export interface IUserTileSmallProps extends HTMLAttributes<HTMLDivElement> {
  user: Pick<IUser, "id" | "first_name" | "last_name" | "gravatar_hash">;
  imageClassName?: string;
  borderClassName?: string;
}

export const UserTileSmall = forwardRef<HTMLDivElement, IUserTileSmallProps>(
  (
    { className, user, imageClassName = "", borderClassName = "", ...props },
    ref
  ) => {
    // Get user's Gravatar hash from the email
    return user ? (
      <Link to={`/users/${user.id}`}>
        <div
          ref={ref}
          className={cn(
            "flex flex-col items-center h-full px-3 py-2 transition border rounded-lg shadow-md grow border-slate-400 dark:border-slate-700 hover:border-white hover:ring-4 hover:ring-blue-400 shadow-gray-200 dark:shadow-gray-800 hover:cursor-pointer",
            className
          )}
          {...props}
        >
          <div
            className={cn(
              "flex items-center justify-center w-16 h-16 border rounded-full border-slate-300 dark:border-slate-700",
              borderClassName
            )}
          >
            {user.gravatar_hash ? (
              <img
                className={cn("rounded-full", imageClassName)}
                src={`https://gravatar.com/avatar/${user.gravatar_hash}?s=64`}
              />
            ) : (
              <FontAwesomeIcon
                className={cn("w-8 h-8 text-slate-400", imageClassName)}
                icon={faUserLarge}
              />
            )}
          </div>
          <div className="mt-2 leading-tight text-center text-slate-500 dark:text-slate-300">
            {user.first_name} {user.last_name}
          </div>
        </div>
      </Link>
    ) : (
      <LoadingSpinnerInline />
    );
  }
);

interface IUserSingleLinkWithAvatar {
  user: Pick<IUser, "id" | "first_name" | "last_name" | "gravatar_hash">;
  gravatarImageClassName?: string;
  elementClassName?: string;
}
export const UserSingleLinkWithAvatar: FC<IUserSingleLinkWithAvatar> = ({
  user,
  gravatarImageClassName = "w-6 h-6 border border-slate-300 dark:border-slate-400 rounded-full",
  elementClassName = "",
}) => (
  <div className={cn("flex items-center flex-1 gap-x-2", elementClassName)}>
    <GravatarAvatar
      profile_identifier={user.gravatar_hash}
      className={gravatarImageClassName}
      desired_image_size={32}
    />
    <Link to={`/users/${user.id}`} className="font-medium link">
      {user.first_name} {user.last_name}
    </Link>
  </div>
);
